export function filterParetoRows(paretoData, hideZeroFailSteps) {
  return hideZeroFailSteps
    ? paretoData.filter((data) => Number(data.failCount || 0) > 0)
    : paretoData;
}

function wrapLabel(text, maxChars = 18, maxLines = 2) {
  const words = String(text || 'No Name').replace(/\s+/g, ' ').trim().split(' ');
  const lines = [];
  let currentLine = '';

  words.forEach((word) => {
    const nextLine = currentLine ? `${currentLine} ${word}` : word;
    if (nextLine.length <= maxChars) {
      currentLine = nextLine;
      return;
    }

    if (currentLine) lines.push(currentLine);
    currentLine = word.length > maxChars ? `${word.slice(0, maxChars - 3)}...` : word;
  });

  if (currentLine) lines.push(currentLine);

  if (lines.length > maxLines) {
    const visibleLines = lines.slice(0, maxLines);
    visibleLines[maxLines - 1] = `${visibleLines[maxLines - 1].slice(0, maxChars - 3)}...`;
    return visibleLines;
  }

  return lines;
}

export function buildParetoChartData(displayedParetoData) {
  const eightyPercentIndex = displayedParetoData.findIndex(
    (data) => Number(data.cumulativePercentage) >= 80
  );
  const fullLabels = displayedParetoData.map(
    (data) => `Step ${data.stepNumber} - ${data.stepName || 'No Name'}`
  );
  return {
    labels: displayedParetoData.map(
      (data) => [String(data.stepNumber), ...wrapLabel(data.stepName)]
    ),
    datasets: [
      {
        label: 'Fail Count',
        data: displayedParetoData.map((data) => Math.round(Number(data.failCount) || 0)),
        backgroundColor: 'rgba(255, 99, 132, 0.6)',
        yAxisID: 'y',
        type: 'bar',
      },
      {
        label: 'Cumulative Fail Percentage',
        data: displayedParetoData.map((data) => data.cumulativePercentage),
        type: 'line',
        fill: false,
        borderColor: 'rgba(54, 162, 235, 0.8)',
        backgroundColor: 'rgba(54, 162, 235, 0.8)',
        yAxisID: 'y1',
        tension: 0.4,
      },
    ],
    eightyPercentIndex,
    fullLabels,
  };
}

export function buildParetoOptions(chartData) {
  const labelCount = chartData?.labels?.length || 0;
  const skipEvery = labelCount > 24 ? Math.ceil(labelCount / 12) : 1;

  return {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: {
        beginAtZero: true,
        title: { display: true, text: 'Fail Count' },
        ticks: {
          precision: 0,
          callback: (value) => Math.round(Number(value)).toString(),
        },
      },
      y1: {
        beginAtZero: true,
        position: 'right',
        grid: { drawOnChartArea: false },
        title: { display: true, text: 'Cumulative Fail Percentage' },
        ticks: {
          callback: (value) => `${Math.round(Number(value))}%`,
        },
        min: 0,
        max: 100,
      },
      x: {
        ticks: {
          autoSkip: false,
          font: { size: 11 },
          maxRotation: 45,
          minRotation: 45,
          padding: 8,
          callback(value, index) {
            if (skipEvery > 1 && index % skipEvery !== 0) return '';
            const label = typeof this.getLabelForValue === 'function'
              ? this.getLabelForValue(value)
              : chartData.labels?.[index];
            return label;
          },
        },
      },
    },
    plugins: {
      legend: { display: true, position: 'top' },
      annotation: {
        annotations:
          chartData.eightyPercentIndex >= 0
            ? {
                eightyPercentLine: {
                  type: 'line',
                  xMin: chartData.eightyPercentIndex,
                  xMax: chartData.eightyPercentIndex,
                  borderColor: 'rgba(255, 165, 0, 0.8)',
                  borderWidth: 2,
                  label: {
                    enabled: true,
                    content: '80% Cumulative',
                    position: 'top',
                    backgroundColor: 'rgba(255, 165, 0, 0.8)',
                    color: '#000',
                  },
                },
              }
            : {},
      },
      tooltip: {
        callbacks: {
          title: (items) => {
            const index = items?.[0]?.dataIndex;
            return chartData.fullLabels?.[index] || '';
          },
          label: (context) => {
            const label = context.dataset.label || '';
            const value = Number(context.raw) || 0;
            if (context.dataset.yAxisID === 'y1') return `${label}: ${value.toFixed(2)}%`;
            return `${label}: ${Math.round(value)}`;
          },
        },
      },
    },
  };
}

export function getDatePreset(preset) {
  const date = new Date();
  if (preset === 'yesterday') date.setDate(date.getDate() - 1);
  if (preset === 'sevenDaysAgo') date.setDate(date.getDate() - 7);
  if (preset === 'thirtyDaysAgo') date.setDate(date.getDate() - 30);
  return date;
}

export function getParetoMetrics(paretoData) {
  const totalSteps = paretoData.length;
  const totalFailures = paretoData.reduce((sum, row) => sum + (Number(row.failCount) || 0), 0);
  const totalCount = paretoData.reduce((sum, row) => sum + (Number(row.totCount) || 0), 0);
  const topStep = paretoData.length ? [...paretoData].sort((a, b) => Number(b.failCount || 0) - Number(a.failCount || 0))[0] : null;
  const failRate = totalCount ? ((totalFailures / totalCount) * 100).toFixed(2) : '0.00';

  return [
    { label: 'Steps shown', value: totalSteps, tone: 'neutral' },
    { label: 'Total records', value: totalCount, tone: 'neutral' },
    { label: 'Failures', value: Math.round(totalFailures), tone: 'fail' },
    { label: 'Fail rate', value: `${failRate}%`, tone: 'fail' },
    { label: 'Top fail step', value: topStep ? `Step ${topStep.stepNumber}` : '-', tone: 'neutral' },
  ];
}
